import { HttpStatusCode } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { TranslocoService } from '@jsverse/transloco';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { Patient } from '@pixacare/domain';
import { FilterOperator, PagedResponse, PaginationQuery } from '@pixacare/shared';
import { PatientHttpService } from '@services/http/patient.http.service';
import { catchError, mergeMap, Observable, switchMap } from 'rxjs';
import { lifeCycleActions } from '../life-cycle/life-cycle.actions';
import { notificationsActions } from '../notifications/notifications.actions';
import { telemonitoringsActions } from '../telemonitorings/telemonitorings.actions';
import { patientsActions } from './patients.actions';

@Injectable()
export class PatientsEffects {
  private readonly actions$ = inject(Actions);
  private readonly patientService = inject(PatientHttpService);
  private readonly translocoService = inject(TranslocoService);

  createPatient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.createPatient),
      mergeMap(({ clientCode, patient }) =>
        this.patientService.createPatient(clientCode, patient).pipe(
          switchMap((newPatient: Patient) => [
            patientsActions.createPatientSuccess({ clientCode, patient: newPatient }),
            notificationsActions.success({
              message: this.translocoService.translate('Le patient a bien été créé.'),
              title: this.translocoService.translate('Patient créé'),
            }),
          ]),
          catchError(({ error, status }) => {
            // The patient already exists, so we redirect to the patient page
            if (status === HttpStatusCode.Conflict) {
              return [lifeCycleActions.redirect({ url: `/client/${clientCode}/patients/${error.patientId}` })];
            }
            return [
              lifeCycleActions.loadError({ error }),
              notificationsActions.error({
                error,
                title: this.translocoService.translate(`Une erreur est survenue.`),
                message: this.translocoService.translate("Nous n'avons pas pu créer le patient."),
              }),
            ];
          }),
        ),
      ),
    ),
  );

  createPatientSuccess$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.createPatientSuccess),
      switchMap(({ clientCode, patient }) => [
        lifeCycleActions.redirect({ url: `/client/${clientCode}/patients/${patient.id}` }),
      ]),
    ),
  );

  getPatient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.getPatient),
      mergeMap(({ clientCode, patientId, departmentId }) =>
        this.patientService
          .getPatients(clientCode, {
            query: new PaginationQuery({
              filter: [
                {
                  prop: 'id',
                  op: FilterOperator.EQUAL,
                  val: patientId.toString(),
                },
                ...(departmentId
                  ? [
                      {
                        prop: 'department_id',
                        op: FilterOperator.EQUAL,
                        val: departmentId.toString(),
                      },
                    ]
                  : []),
              ],
            }),
          })
          .pipe(
            switchMap((collection: PagedResponse<Patient>) => [
              patientsActions.getPatientSuccess({ clientCode, collection }),
            ]),
            catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
          ),
      ),
    ),
  );

  getNextPatients$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.getNextPatients),
      mergeMap(({ clientCode, options: { query, reset } }) =>
        this.patientService.getPatients(clientCode, { query }).pipe(
          switchMap((collection: PagedResponse<Patient>) => [
            patientsActions.getNextPatientsSuccess({ clientCode, reset, collection }),
          ]),
          catchError((error: Error) => [lifeCycleActions.loadError({ error })]),
        ),
      ),
    ),
  );

  updatePatient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.updatePatient),
      mergeMap(({ clientCode, patientId, patient }) =>
        this.patientService.updatePatient(clientCode, patientId, patient).pipe(
          switchMap((updatedPatient: Patient) => [
            patientsActions.updatePatientSuccess({ clientCode, patient: updatedPatient }),
            notificationsActions.success({
              title: this.translocoService.translate('Le patient a bien été modifié.'),
              message: this.translocoService.translate('Patient modifié'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu modifier le patient."),
            }),
          ]),
        ),
      ),
    ),
  );

  mergePatients$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.mergePatients),
      mergeMap(({ clientCode, patientId, targetPatientId }) =>
        this.patientService.mergePatients(clientCode, patientId, targetPatientId).pipe(
          switchMap(() => [
            patientsActions.removePatient({ clientCode, patientId }),
            telemonitoringsActions.updateTelemonitoringPatient({ patientId, targetPatientId }),
            notificationsActions.success({
              title: this.translocoService.translate('La fusion des patients a été effectuée'),
              message: this.translocoService.translate('Le patient a été supprimé'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu fusionner les patients."),
            }),
          ]),
        ),
      ),
    ),
  );

  archivePatient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.archivePatient),
      mergeMap(({ clientCode, patientId }) =>
        this.patientService.archive(clientCode, patientId).pipe(
          switchMap((archivedPatient: Patient) => [
            patientsActions.archivePatientSuccess({
              clientCode,
              patient: archivedPatient,
            }),
            notificationsActions.success({
              title: this.translocoService.translate('Le patient a bien été archivé.'),
              message: this.translocoService.translate('Patient archivé'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu archiver le patient."),
            }),
          ]),
        ),
      ),
    ),
  );

  unarchivePatient$: Observable<Action> = createEffect(() =>
    this.actions$.pipe(
      ofType(patientsActions.unarchivePatient),
      mergeMap(({ clientCode, patientId }) =>
        this.patientService.unarchive(clientCode, patientId).pipe(
          switchMap((unarchivedPatient: Patient) => [
            patientsActions.unarchivePatientSuccess({
              clientCode,
              patient: unarchivedPatient,
            }),
            notificationsActions.success({
              title: this.translocoService.translate('Le patient a bien été désarchivé.'),
              message: this.translocoService.translate('Patient désarchivé'),
            }),
          ]),
          catchError((error: Error) => [
            lifeCycleActions.loadError({ error }),
            notificationsActions.error({
              error,
              title: this.translocoService.translate(`Une erreur est survenue.`),
              message: this.translocoService.translate("Nous n'avons pas pu désarchiver le patient."),
            }),
          ]),
        ),
      ),
    ),
  );
}
